/** Max profile photo size before encoding (2 MiB). */
export const PROFILE_PHOTO_MAX_BYTES = 2 * 1024 * 1024;

/** Max tenant logo size before encoding (512 KiB, API limit). */
export const TENANT_LOGO_MAX_BYTES = 512 * 1024;

const DATA_URL_PATTERN = /^data:([^;]+);base64,([\s\S]+)$/i;

/**
 * API stores raw base64 (no data-URL prefix). Convert a FileReader data URL
 * or already-raw payload into that form.
 */
export function toPhotoPayload(value: string | null | undefined): string {
  if (value == null) {
    return '';
  }

  const trimmed = value.trim();
  if (trimmed === '') {
    return '';
  }

  const dataUrlMatch = DATA_URL_PATTERN.exec(trimmed);
  if (dataUrlMatch) {
    return dataUrlMatch[2] ?? '';
  }

  return trimmed;
}

/** True when a downloaded blob is safe to use as an `<img src>` (blob URL). */
export function isDisplayablePhotoBlob(
  blob: Blob | null | undefined,
): blob is Blob {
  if (blob == null || blob.size === 0) {
    return false;
  }

  if (blob.type && !blob.type.startsWith('image/')) {
    // Photo downloads often omit a specific image/* type.
    if (blob.type !== 'application/octet-stream') {
      return false;
    }
  }

  return true;
}

/** Build an `<img src>` value from API photo (raw base64, data URL, or URL). */
export function toPhotoDisplaySrc(
  value: string | null | undefined,
): string | null {
  if (value == null) {
    return null;
  }

  const trimmed = value.trim();
  if (trimmed === '') {
    return null;
  }

  if (
    trimmed.startsWith('data:') ||
    trimmed.startsWith('http://') ||
    trimmed.startsWith('https://') ||
    trimmed.startsWith('blob:')
  ) {
    return trimmed;
  }

  const mimeType = trimmed.startsWith('iVBOR')
    ? 'image/png'
    : trimmed.startsWith('R0lGOD')
      ? 'image/gif'
      : trimmed.startsWith('UklGR')
        ? 'image/webp'
        : 'image/jpeg';

  return `data:${mimeType};base64,${trimmed}`;
}

export function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result);
        return;
      }
      reject(new Error('Could not read image file'));
    };
    reader.onerror = () => {
      reject(reader.error ?? new Error('Could not read image file'));
    };
    reader.readAsDataURL(file);
  });
}

export function logoToFormValue(
  logo: Blob | string | null | undefined,
): string | null {
  if (logo == null) {
    return null;
  }

  if (typeof logo === 'string') {
    const payload = toPhotoPayload(logo);
    return payload === '' ? null : payload;
  }

  return null;
}

export function resolveLogoSrc(logo: Blob | string | null | undefined): {
  src: string | null;
  revokeUrl: string | null;
} {
  if (logo == null) {
    return { src: null, revokeUrl: null };
  }

  if (typeof logo === 'string') {
    return { src: toPhotoDisplaySrc(logo), revokeUrl: null };
  }

  if (typeof Blob !== 'undefined' && logo instanceof Blob) {
    if (!isDisplayablePhotoBlob(logo)) {
      return { src: null, revokeUrl: null };
    }
    const objectUrl = URL.createObjectURL(logo);
    return { src: objectUrl, revokeUrl: objectUrl };
  }

  return { src: null, revokeUrl: null };
}

/** User list/icon preview from API `thumbnail` (JSON base64 string or Blob). */
export function resolveUserThumbnailSrc(
  thumbnail: Blob | string | null | undefined,
): {
  src: string | null;
  revokeUrl: string | null;
} {
  return resolveLogoSrc(thumbnail);
}
